import { useState } from "react"
import { Badge, Button, Card, ErrorNote, Field, PageHeader, Spinner, useLoad, useToast } from "../components/ui"
import { api } from "../lib/api"

// Deployment addresses, editable without a redeploy.
//
// Three values, but the first two decide whether sessions are billed at all —
// unset, a session's inference falls back to the agent's bundled provider and
// is metered by nobody. That is why this is a screen and not a compose file.

type Setting = {
  key: string
  envName: string
  value: string
  // Set for secret values: what `value` holds is a mask, and saving without
  // retyping must not overwrite the stored blob with it.
  masked?: boolean
  source: "database" | "env" | "unset"
  updatedAt: string | null
}

const LABELS: Record<string, { label: string; hint: string; placeholder: string }> = {
  agentApiUrl: {
    label: "Agent API URL",
    hint: "Where a session container reaches this platform's API. Must be reachable from the container hosts — not localhost.",
    placeholder: "http://10.116.0.3:3000/api",
  },
  agentGatewayUrl: {
    label: "Agent gateway URL",
    hint: "Where a session container sends its AI requests. Unset means sessions run unmetered — nobody is billed.",
    placeholder: "http://10.116.0.3:8080",
  },
  platformPublicIp: {
    label: "Platform public IP",
    hint: "The address customers allow through their firewall so their own container servers accept our connections.",
    placeholder: "164.92.130.44",
  },
  googleOAuthClientId: {
    label: "Google OAuth client ID",
    hint: "The Google app employees consent through when connecting Google Drive. Create one in the Google Cloud Console (OAuth client, Web application) with redirect URI APP_URL + /api/google-drive/callback.",
    placeholder: "1234-abc.apps.googleusercontent.com",
  },
  googleOAuthClientSecret: {
    label: "Google OAuth client secret",
    hint: "Stored encrypted, shown masked. Clearing falls back to the GOOGLE_OAUTH_CLIENT_SECRET environment variable.",
    placeholder: "GOCSPX-…",
  },
  shopifyOAuthClientId: {
    label: "Shopify OAuth client ID",
    hint: "The Shopify app employees consent through when connecting a store. Create it in the Partner Dashboard and register redirect URI APP_URL + /api/shopify/callback.",
    placeholder: "a1b2c3d4e5…",
  },
  shopifyOAuthClientSecret: {
    label: "Shopify OAuth client secret",
    hint: "Stored encrypted, shown masked. Clearing falls back to the SHOPIFY_OAUTH_CLIENT_SECRET environment variable.",
    placeholder: "shpss_…",
  },
  shopifyOAuthScopes: {
    label: "Shopify OAuth scopes",
    hint: "Comma-separated Admin API scopes the connect asks for. Empty uses the default product, inventory, content and theme scopes; order and customer scopes need Shopify's protected-data approval.",
    placeholder: "read_products,write_products,…",
  },
}

const SOURCE: Record<Setting["source"], { tone: "success" | "neutral" | "warning"; label: string }> = {
  database: { tone: "success", label: "Saved here" },
  env: { tone: "neutral", label: "From the environment" },
  unset: { tone: "warning", label: "Not set" },
}

export function PlatformSettingsPage() {
  const { data, loading, error, reload } = useLoad(() =>
    api.get<{ settings: Setting[] }>("/platform/settings"),
  )

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Deployment addresses. Changes apply to new sessions within half a minute — no restart."
      />
      <ErrorNote error={error} />
      {loading ? (
        <Spinner />
      ) : data ? (
        <div className="flex max-w-xl flex-col gap-4">
          {data.settings.map((s) => (
            <SettingCard key={s.key} setting={s} onSaved={reload} />
          ))}
        </div>
      ) : null}
    </>
  )
}

function SettingCard({ setting, onSaved }: { setting: Setting; onSaved: () => void }) {
  const toast = useToast()
  const meta = LABELS[setting.key] ?? { label: setting.key, hint: "", placeholder: "" }
  const source = SOURCE[setting.source]
  // A masked value starts empty: the field shows the mask as placeholder text,
  // and an untouched save sends "" — which setSetting treats as "clear". So a
  // masked key only saves when the user actually typed a new secret, and the
  // Save button stays disabled until they do.
  const [value, setValue] = useState(setting.masked ? "" : setting.value)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function save() {
    setSaving(true)
    setError(null)
    try {
      await api.put(`/platform/settings/${setting.key}`, { value })
      toast(value.trim() ? `${meta.label} saved` : `${meta.label} cleared`)
      onSaved()
    } catch (err) {
      setError(err)
    } finally {
      setSaving(false)
    }
  }

  const dirty = setting.masked ? value.trim().length > 0 : value !== setting.value

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-foreground">{meta.label}</span>
        <Badge tone={source.tone}>{source.label}</Badge>
      </div>
      <p className="text-xs text-muted">{meta.hint}</p>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Field
            label={setting.envName}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={setting.masked && setting.value ? setting.value : meta.placeholder}
          />
        </div>
        <Button onClick={() => void save()} disabled={saving || !dirty}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
      {/* Clearing goes back to the env var, not to broken — worth saying. */}
      {setting.source === "database" && !setting.masked && (
        <p className="text-xs text-muted">
          Clearing this field falls back to the {setting.envName} environment variable.
        </p>
      )}
      {setting.masked && (
        <p className="text-xs text-muted">
          Leave empty to keep the stored secret. Type a new value to replace it; clearing entirely falls back to the {setting.envName} environment variable.
        </p>
      )}
      <ErrorNote error={error} />
    </Card>
  )
}
